import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { FormikForm } from "components/FormikForm/FormikForm";
import * as RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES from "constants/recurringJorunalEntryUpdateTypes";
import { FormikValues } from "formik";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import { useBulkDeleteJournalEntriesMutation } from "store/apis/journalEntry";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { pluralize } from "utils/pluralize";
import { SelectedRow } from "./TableSelectionControls";
type BulkJEDeleteModalProps = {
  allSelectedRows: SelectedRow[];
  groupId: string;
  entityId: string;
  onSuccess?: () => void;
} & ModalProps;

function DeleteMessage({
  allSelectedRows,
}: {
  allSelectedRows: SelectedRow[];
}) {
  const total = allSelectedRows.length;
  const recurring = allSelectedRows.filter(
    (r) => r.is_recurring_enabled
  ).length;

  const payroll = allSelectedRows.filter((r) => r.linked_payroll_id).length;

  let msg = `You are about to permanently delete ${pluralize(
    total,
    "journal entry",
    "journal entries"
  )}`;

  if (recurring === 1 && total === 1) {
    return `This is a recurring journal entry, this action will delete:`;
  }

  if (payroll === 1 && total === 1) {
    return `This is a payroll journal entry. This will permanently delete the journal entry. This action cannot be undone.`;
  }

  if (recurring === 0 && payroll === 0) {
    msg = `${msg}.`;
  }

  if (recurring > 0 && payroll === 0) {
    msg = `${msg}, including ${pluralize(
      recurring,
      "recurring journal entry",
      "recurring journal entries"
    )}.`;
  }

  if (payroll > 0 && recurring === 0) {
    msg = `${msg}, including ${pluralize(
      payroll,
      "payroll journal entry",
      "payroll journal entries"
    )}.`;
  }

  if (recurring > 0 && payroll > 0) {
    msg = `${msg}, including ${pluralize(
      recurring,
      "recurring journal entry",
      "recurring journal entries"
    )} and ${pluralize(
      payroll,
      "payroll journal entry",
      "payroll journal entries"
    )}.`;
  }

  return <>{msg} This action cannot be undone.</>;
}

export default function BulkJEDeleteModal({
  isOpen,
  close,
  allSelectedRows,
  groupId,
  entityId,
  onSuccess,
}: BulkJEDeleteModalProps) {
  const { alertToast, successToast } = useToast();
  const [bulkDeleteJournalEntries, { isLoading }] =
    useBulkDeleteJournalEntriesMutation();

  const selectedJournalEntryIds = useMemo(
    () => allSelectedRows.map(({ uuid }) => uuid),
    [allSelectedRows]
  );

  const handleBulkDelete = async (formikValues: FormikValues) => {
    const { event } = formikValues as {
      event: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
    };

    if (selectedJournalEntryIds.length === 0) {
      alertToast({ message: "Please select journal entries to delete." });
      return;
    }

    try {
      await bulkDeleteJournalEntries({
        groupId,
        entityId,
        entryIds: selectedJournalEntryIds,
        event_update_type: event,
      }).unwrap();

      successToast({
        message: `${pluralize(
          selectedJournalEntryIds.length,
          "journal entry",
          "journal entries"
        )} deleted successfully!`,
      });
      close();
      onSuccess?.();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const recurring = allSelectedRows.filter(
    (r) => r.is_recurring_enabled
  ).length;

  const payroll = allSelectedRows.filter((r) => r.linked_payroll_id).length;

  return (
    <FormikForm
      onSubmit={handleBulkDelete}
      initialValues={{
        event: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
      }}
    >
      {({ submitForm, setFieldValue, values }) => (
        <>
          <Modal.Body className="t-flex t-flex-col t-gap-4">
            <div className="t-text-body-lg t-text-text-60">
              <DeleteMessage allSelectedRows={allSelectedRows} />
            </div>
            {recurring > 0 && (
              <div className="t-text-text-60">
                <p className="t-text-body-lg">Choose an option:</p>
                <RadioGroup.Root
                  value={values.event}
                  onValueChange={(v) => setFieldValue("event", v)}
                  className="t-flex t-flex-col t-gap-4"
                >
                  <RadioGroup.Item
                    value={RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT}
                  >
                    <span className="t-text-body-sm">
                      Delete only these entries
                    </span>
                  </RadioGroup.Item>
                  <RadioGroup.Item
                    value={
                      RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT_AND_FOLLOWING
                    }
                  >
                    <span className="t-text-body-sm">
                      Delete these and all future entries
                    </span>
                  </RadioGroup.Item>
                </RadioGroup.Root>
                <p className="t-text-text-30 t-text-body-sm t-italic t-m-0 t-mt-3">
                  Note: This won't affect previous entries.
                </p>
              </div>
            )}

            {payroll > 0 && (
              <p className="t-text-text-30 t-text-body-sm t-italic t-m-0">
                Note: {pluralize(payroll, "entry", "entries")} selected has a
                linked payroll report, which will also be permanently deleted.
              </p>
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button disabled={isLoading}>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="danger"
              onClick={submitForm}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Delete
            </Button>
          </Modal.FooterButtonGroup>
        </>
      )}
    </FormikForm>
  );
}
